/**
 * Générateur — Il factorise une expression du type a² − b² et développe des
 * expressions du type (a + b)(a − b).
 * Attendu ID : 210 | Niveau : 3ème | Domaine : Calcul littéral (d12, O10)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A7S2-A7S4). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « developper »  — (ax + b)(ax − b), y compris DÉGUISÉ par l'ordre des termes
 *   « nues »        — factoriser x² − 49, 16x² − 36, 81 − t² (formes nues)
 *   « expressions » — a et b sont des expressions : (ax + c)² − k², étape
 *                     « b = … » imposée puis les deux facteurs
 *   « rapide »      — calcul rapide m² − n² sans calculer les carrés
 *
 * Les fractions de l'Ex.12 (étoile « vers la seconde ») sont écartées :
 * réservées aux ambitieux selon le doc, saisies litigieuses.
 */

import {
    randomChoice, randInt, tex,
    makeTexteTrou,
} from '../../../utils';
import { fmtBinome, carre } from './litteral';

const ATTENDU_ID = 210;

// Contrainte d'écriture des saisies : dite en phrase (FORMATS_TANOED §0.1).
const CONSIGNE_SIGNE = ' Écris les valeurs avec leur signe.';
// Identité remarquable, rendue une fois pour toutes.
const IDENTITE = tex('(a + b)(a - b) = a^{2} - b^{2}');

const L_DEVELOPPER = 'Développer (a + b)(a − b)';
const L_NUES = 'Factoriser a² − b² (formes simples)';
const L_EXPRESSIONS = 'Factoriser quand a et b sont des expressions';
const L_RAPIDE = 'Calculer astucieusement avec a² − b²';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const LETTRES = ['x', 'y', 't', 'z'];

// ═════════ « developper » — (ax + b)(ax − b), formes déguisées ═════════

const genDevelopperTrous = () => {
    const L = randomChoice(LETTRES);
    const a = randInt(1, 7); const b = randInt(1, 9);
    const forme = randomChoice(['standard', 'inverse', 'deguise']);
    let gauche; let A; let C;
    if (forme === 'standard') {
        // (ax + b)(ax − b) = a²x² − b²
        gauche = `${fmtBinome(a, b, L)}${fmtBinome(a, -b, L)}`;
        A = a * a; C = -b * b;
    } else if (forme === 'inverse') {
        // (b + ax)(ax − b) ordre inversé dans le 1er facteur → a²x² − b²
        const ax = `${a === 1 ? '' : a}${L}`;
        gauche = `\\left(${b} + ${ax}\\right)\\left(${ax} - ${b}\\right)`;
        A = a * a; C = -b * b;
    } else {
        // (b − ax)(b + ax) = b² − a²x²
        const ax = `${a === 1 ? '' : a}${L}`;
        gauche = `\\left(${b} - ${ax}\\right)\\left(${b} + ${ax}\\right)`;
        A = -a * a; C = b * b;
    }
    const phrase = `$E = {{${A}}}${carre(L)} + {{${C}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`E = ${gauche}`)}.</p>`
        + `<p><b>Développe avec l'identité ${IDENTITE}.</b>${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_DEVELOPPER), mode: 'saisie' });
    payload._meta.params = { forme, a, b, A, C };
    return payload;
};

// ═════════ « nues » — factoriser les formes nues ═════════

const genNuesTrous = () => {
    const L = randomChoice(LETTRES);
    const forme = randomChoice(['x2_k2', 'p2x2_k2', 'k2_x2']);
    let intro; let phrase; let params;
    if (forme === 'x2_k2') {
        // x² − k² = (x + k)(x − k)
        const k = randInt(2, 12);
        intro = `M = ${carre(L)} - ${k * k}`;
        phrase = `$M = \\left(${L} + {{${k}}}\\right)\\left(${L} - {{${k}}}\\right)$`;
        params = { forme, k };
    } else if (forme === 'p2x2_k2') {
        // p²x² − k² = (px + k)(px − k)
        const p = randInt(2, 7); const k = randInt(2, 9);
        intro = `P = ${p * p}${carre(L)} - ${k * k}`;
        phrase = `$P = \\left({{${p}}}${L} + {{${k}}}\\right)\\left({{${p}}}${L} - {{${k}}}\\right)$`;
        params = { forme, p, k };
    } else {
        // k² − x² = (k + x)(k − x)
        const k = randInt(2, 12);
        intro = `N = ${k * k} - ${carre(L)}`;
        phrase = `$N = \\left({{${k}}} + ${L}\\right)\\left({{${k}}} - ${L}\\right)$`;
        params = { forme, k };
    }
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(intro)}.</p>`
        + `<p><b>Reconnais les deux carrés, puis factorise avec ${tex('a^{2} - b^{2} = (a + b)(a - b)')}.</b></p>`,
        { ...opts(L_NUES), mode: 'saisie' });
    payload._meta.params = params;
    return payload;
};

// ═════════ « expressions » — (ax + c)² − k², étape imposée ═════════

const genExpressionsTrous = () => {
    const L = randomChoice(LETTRES);
    let a; let c; let k; let s1; let s2; let ok = false;
    for (let essai = 0; essai < 60 && !ok; essai++) {
        a = randInt(1, 4); c = randInt(-9, 9); k = randInt(2, 12);
        s1 = c + k; s2 = c - k;
        if (c !== 0 && s1 !== 0 && s2 !== 0) ok = true;    // s1 ≠ s2 garanti (k ≥ 2)
    }
    if (!ok) { a = 1; c = 4; k = 7; s1 = 11; s2 = -3; }    // repli (doc R)
    const aAff = a === 1 ? '' : a;
    const aExpr = `${aAff}${L} ${c < 0 ? '-' : '+'} ${Math.abs(c)}`;
    const intro = `R = \\left(${aExpr}\\right)^{2} - ${k * k}`;
    const phrase = `$b = {{${k}}} \\qquad R = \\left(${aAff}${L} + {{${s1}}}\\right)\\left(${aAff}${L} + {{${s2}}}\\right)$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(intro)}. Ici ${tex(`a = ${aExpr}`)} et b est un nombre.</p>`
        + `<p><b>Donne b, puis factorise ${tex('R = (a + b)(a - b)')} et réduis chaque `
        + `facteur.</b>${CONSIGNE_SIGNE}</p>`,
        { ...opts(L_EXPRESSIONS), mode: 'saisie' });
    payload._meta.params = { a, c, k, s1, s2 };
    return payload;
};

// ═════════ « rapide » — calcul mental m² − n² ═════════

const genRapideTrous = () => {
    let m; let n; let d;
    for (let essai = 0; essai < 40; essai++) {
        d = randomChoice([1, 2, 5, 10]);
        n = randInt(40, 400);
        m = n + d;
        if ((m + n) % 10 === 0 || d === 1) break;         // somme « ronde » ou écart 1
    }
    const res = d * (m + n);
    const phrase = `$${m}^{2} - ${n}^{2} = {{${d}}} \\times {{${m + n}}} = {{${res}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut calculer ${tex(`${m}^{2} - ${n}^{2}`)} SANS calculer les carrés.</p>`
        + `<p><b>Utilise ${tex('m^{2} - n^{2} = (m - n)(m + n)')}, puis conclus.</b></p>`,
        { ...opts(L_RAPIDE), mode: 'saisie' });
    payload._meta.params = { m, n, d, res };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    developper_texte_trou_saisie: genDevelopperTrous,
    nues_texte_trou_saisie: genNuesTrous,
    expressions_texte_trou_saisie: genExpressionsTrous,
    rapide_texte_trou_saisie: genRapideTrous,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'developper', label: L_DEVELOPPER, formatsList: buildFormats('developper') },
    { id: 'nues', label: L_NUES, formatsList: buildFormats('nues') },
    { id: 'expressions', label: L_EXPRESSIONS, formatsList: buildFormats('expressions') },
    { id: 'rapide', label: L_RAPIDE, formatsList: buildFormats('rapide') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
